import React from 'react';
import PropTypes from 'prop-types';
import { FiCornerUpLeft, FiLock, FiRefreshCw, FiRotateCcw } from 'react-icons/fi';
import { confirmAlert } from 'components/common';
import { escapeHtml } from 'helpers/utils';
import SettingValueField from './SettingValueField';
import { groupMeta, isJsonSetting, isUnset, prettyJson } from './constants';

// ── Status tag ────────────────────────────────────────────────────────────────

const TONES = {
  unset:      'tw-bg-amber-50 tw-text-amber-700 tw-border-amber-200',
  overridden: 'tw-bg-violet-50 tw-text-violet-700 tw-border-violet-200',
  sensitive:  'tw-bg-slate-100 tw-text-slate-600 tw-border-slate-200',
  dirty:      'tw-bg-primary-50 tw-text-primary-700 tw-border-primary-200',
  group:      'tw-bg-white tw-text-slate-600 tw-border-slate-200',
};

const Tag = ({ tone, children, title }) => (
  <span
    title={title}
    className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-1.5 tw-py-0.5 tw-rounded tw-border tw-text-[10px] tw-font-semibold tw-whitespace-nowrap ${TONES[tone]}`}
  >
    {children}
  </span>
);
Tag.propTypes = { tone: PropTypes.string.isRequired, children: PropTypes.node, title: PropTypes.string };

// ── Row ───────────────────────────────────────────────────────────────────────

const SettingRow = ({ setting, pendingValue, onValueChange, onRevert, onReset, showGroupTag, onSync, syncing }) => {
  // JSON is pretty-printed for reading, but compared against the same
  // pretty-printed form — reformatting alone must never look like an edit.
  const serverValue = isJsonSetting(setting) ? prettyJson(setting.value || '') : (setting.value || '');
  const dirty = pendingValue !== undefined;
  const value = dirty ? pendingValue : serverValue;

  const unset = isUnset(setting) && !dirty;
  const meta = groupMeta(setting.group);

  const handleChange = (next) => onValueChange(setting.key, next, next === serverValue);

  // A synced key is filled by its own button, so "reset to default" there would
  // offer to clear what the sync just fetched.
  const canReset = setting.overridden && setting.is_editable && !onSync;

  const handleReset = () => confirmAlert({
    title: 'Reset to default',
    message: `Reset <strong>${escapeHtml(setting.key)}</strong> to its .env default? The stored value is cleared.`,
    type: 'warning',
    buttons: [
      { label: 'Reset', variant: 'primary', onClick: () => onReset(setting.key) },
      { label: 'Cancel', onClick: () => {} },
    ],
  });

  return (
    <div
      className={`tw-flex tw-items-stretch tw-border-b tw-border-slate-100 last:tw-border-0 tw-transition-colors ${
        dirty ? 'tw-bg-primary-50/40' : 'hover:tw-bg-slate-50/70'
      }`}
    >
      <div className={`tw-w-0.5 tw-shrink-0 ${dirty ? 'tw-bg-primary-500' : 'tw-bg-transparent'}`} />

      <div className="tw-flex-1 tw-min-w-0 tw-grid tw-gap-x-6 tw-gap-y-3 tw-px-5 tw-py-4 tw-grid-cols-1 lg:tw-grid-cols-[minmax(0,34%)_minmax(0,1fr)]">

        {/* Key / meaning */}
        <div className="tw-min-w-0">
          <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
            {setting.is_sensitive && <FiLock size={11} className="tw-text-slate-400 tw-shrink-0" />}
            <span className="tw-font-mono tw-text-[12.5px] tw-font-semibold tw-text-slate-800 tw-break-all tw-leading-snug">
              {setting.key}
            </span>
          </div>

          {(setting.label || setting.description) && (
            <p className="tw-text-[12px] tw-text-slate-500 tw-leading-snug tw-mb-1.5">
              {setting.label || setting.description}
            </p>
          )}

          <div className="tw-flex tw-flex-wrap tw-gap-1">
            {dirty && (
              <Tag tone="dirty">
                Unsaved
                <button
                  type="button"
                  onClick={() => onRevert(setting.key)}
                  title="Revert this change"
                  aria-label={`Revert ${setting.key}`}
                  className="tw-ml-0.5 tw-p-0 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-text-primary-600 hover:tw-text-primary-800 tw-flex tw-items-center"
                >
                  <FiCornerUpLeft size={10} />
                </button>
              </Tag>
            )}
            {unset && <Tag tone="unset" title="No value stored and nothing in .env">Not set</Tag>}
            {setting.overridden && <Tag tone="overridden" title="Stored in the database, overriding the .env value">Overridden</Tag>}
            {setting.is_sensitive && <Tag tone="sensitive" title="Encrypted at rest; the API only ever returns a mask">Encrypted</Tag>}
            {showGroupTag && (
              <Tag tone="group">
                <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full" style={{ background: meta.dot }} />
                {setting.group}
              </Tag>
            )}
          </div>
        </div>

        {/* Value */}
        <div className="tw-min-w-0">
          <SettingValueField setting={setting} value={value} dirty={dirty} onChange={handleChange} />

          {(onSync || canReset) && (
          <div className="tw-flex tw-items-center tw-gap-3 tw-mt-2 tw-flex-wrap">
            {onSync && (
              <button
                type="button"
                onClick={onSync}
                disabled={syncing}
                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-semibold tw-text-slate-700 tw-border tw-border-slate-200 tw-rounded-lg hover:tw-bg-slate-50 tw-bg-white tw-cursor-pointer tw-transition-colors disabled:tw-opacity-50"
              >
                <FiRefreshCw size={11} className={syncing ? 'tw-animate-spin' : ''} />
                {syncing ? 'Syncing…' : 'Sync from Opla'}
              </button>
            )}
            {canReset && (
              <button
                type="button"
                onClick={handleReset}
                className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-text-slate-500 hover:tw-text-slate-800 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-p-0 tw-transition-colors"
              >
                <FiRotateCcw size={10} /> Reset to default
              </button>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

SettingRow.propTypes = {
  setting: PropTypes.shape({
    key: PropTypes.string.isRequired,
    value: PropTypes.string,
    label: PropTypes.string,
    description: PropTypes.string,
    group: PropTypes.string,
    data_type: PropTypes.string,
    value_type: PropTypes.string,
    is_sensitive: PropTypes.bool,
    is_editable: PropTypes.bool,
    overridden: PropTypes.bool,
  }).isRequired,
  pendingValue: PropTypes.string,
  onValueChange: PropTypes.func.isRequired,
  onRevert: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  showGroupTag: PropTypes.bool,
  onSync: PropTypes.func,
  syncing: PropTypes.bool,
};

export default SettingRow;
